'use client';

import { ColumnDef } from '@tanstack/react-table';

import { Price } from '@/components/atoms/price';
import { Link } from '@/components/link';
import { ServiceTypeTemplate } from '@/components/providers/service-templates-provider';
import { useSkillById } from '@/components/providers/v1-office-hooks';
import { Badge } from '@/components/ui/badge';

export const SERVICE_TYPE_TEMPLATE_COLUMNS: ColumnDef<ServiceTypeTemplate>[] = [
  {
    accessorKey: 'name',
    header: () => <div className='p-4'>Name</div>,
    cell: ({ row }) => {
      return (
        <Link href={`/settings/service_types/${row.original.id}`} className='p-4 font-medium hover:underline'>
          {row.original.name}
        </Link>
      );
    },
  },
  {
    header: 'Status',
    accessorKey: 'status',
    cell: ({ row }) => {
      const status = row.original.status;
      return (
        <Badge variant={status === 'ACTIVE' ? 'outline' : 'secondary'} className='capitalize'>
          {status.toLowerCase()}
        </Badge>
      );
    },
  },
  {
    header: 'Price',
    accessorKey: 'prices',
    cell: ({ row: { original } }) => {
      const price = original.prices.find((v) => v.default) || original.prices[0];
      if (!price) {
        return '-';
      }
      return (
        <Badge variant='secondary'>
          <Price amount={price.amount} currency={price.currency} />
        </Badge>
      );
    },
  },
  {
    header: 'Frequency',
    accessorKey: 'frequency_unit',
    cell: ({ row }) => {
      const { frequency_unit, frequency_value } = row.original;
      if (frequency_unit === 'MANUAL' || !frequency_value) {
        return '-';
      }
      const label = (() => {
        if (frequency_value === 1) {
          return frequency_unit.toLowerCase();
        }
        return `Every ${frequency_value} ${frequency_unit.toLowerCase()}s`;
      })();

      return <Badge variant='secondary'>{label}</Badge>;
    },
  },
  {
    header: 'Skills',
    accessorKey: 'skills',
    cell: ({ row }) => {
      return <Skills serviceTypeTemplate={row.original} />;
    },
  },
];

function Skills({ serviceTypeTemplate }: { serviceTypeTemplate: ServiceTypeTemplate }) {
  const notShown = serviceTypeTemplate.skills.length - 3;
  return (
    <div className='flex max-w-64 min-w-24 flex-wrap gap-2'>
      {serviceTypeTemplate.skills.slice(0, 3).map((skill) => (
        <SkillBadge key={skill.skill_id} skill_id={skill.skill_id} />
      ))}
      {notShown > 0 && <Badge variant='outline'>+{notShown}</Badge>}
    </div>
  );
}

function SkillBadge({ skill_id }: { skill_id: string }) {
  const skill = useSkillById(skill_id);
  if (!skill) return null;
  return <Badge variant='outline'>{skill.name}</Badge>;
}
